import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Account, LocziApi, PendingTerm, User } from '@loczi/api'

/**
 * Sessão LOCZI: mesma identidade para Web/PWA/Admin (§5.2, §12.6 adendo).
 *
 * - A conta corrente vive NESTE cliente (por instância), espelhada no
 *   header X-Account-Id — nunca numa sessão global que troque outras
 *   abas/instalações silenciosamente.
 * - switchAccount() cancela contexto anterior: aborta requisições em voo
 *   e zera caches em memória antes de carregar a nova conta.
 */
export const useSessionStore = defineStore('loczi.session', () => {
  const api = ref<LocziApi | null>(null)
  const user = ref<User | null>(null)
  const currentAccount = ref<Account | null>(null)
  const ready = ref(false)
  const switching = ref(false)

  /** AbortControllers por requisição — cancelados na troca de conta/logout. */
  const inflight = new Set<AbortController>()

  const isAuthenticated = computed(() => user.value !== null)
  const accounts = computed(() => user.value?.accounts ?? [])
  const canManageTags = computed(() =>
    currentAccount.value?.capabilities?.manage_tags ?? false)
  const canManageGeofences = computed(() =>
    currentAccount.value?.capabilities?.manage_geofences ?? false)
  const canViewBilling = computed(() =>
    currentAccount.value?.capabilities?.view_billing ?? false)
  const canManageAccount = computed(() =>
    currentAccount.value?.capabilities?.manage_account ?? false)

  // ---- Billing / direitos (SPEC-BILLING §3 — fonte: GET /account) -----------
  const billing = computed(() => currentAccount.value?.billing ?? null)
  const entitlements = computed(() => currentAccount.value?.entitlements ?? null)
  const usage = computed(() => currentAccount.value?.usage ?? null)
  const team = computed(() => currentAccount.value?.team ?? null)
  const access = computed(() => currentAccount.value?.access ?? null)
  const contracted = computed(() => currentAccount.value?.contracted ?? null)
  const pendingTerms = computed(() => currentAccount.value?.pending_terms ?? [])

  /**
   * Painel bloqueado: tela cheia, só /conta e /pedidos navegáveis.
   * `blocked` cobre suspenso/frozen/cancelado E fatura em aberto (overdue/
   * pending) e conta sem serviço; `can_serve === false` é o fallback para
   * payload antigo do backend (mesma flag, nome anterior).
   */
  const serviceBlocked = computed(() =>
    billing.value !== null && (billing.value.blocked ?? billing.value.can_serve === false))
  /** Conta do usuário inexistente (ex.: cadastro via Google) → tela "contratar". */
  const hasNoAccount = computed(() => isAuthenticated.value && accounts.value.length === 0)
  /** Inadimplente que ainda vê o painel — hoje vazio (overdue bloqueia). */
  const billingGrace = computed(() =>
    billing.value?.reason === 'overdue' && billing.value.can_serve === true
    && billing.value.blocked !== true)
  /** Cortesia ativa (dias grátis dados pelo suporte). */
  const courtesyActive = computed(() => {
    const until = billing.value?.courtesy_until
    return until !== null && until !== undefined && Date.parse(until) > Date.now()
  })

  /**
   * Direito disponível? cap = chave de capacidade ('mobile', 'tag', 'geofence').
   * null no limite = ilimitado; sem entitlement = sem direito.
   */
  function canUse(cap: 'mobile' | 'tag' | 'geofence'): boolean {
    const e = entitlements.value
    const u = usage.value
    if (!e || !u) return false
    const limit = cap === 'mobile' ? e.mobile_limit : cap === 'tag' ? e.tag_limit : e.geofence_limit
    const used = cap === 'mobile' ? u.mobile : cap === 'tag' ? u.tags : u.geofences
    return limit === null || used < limit
  }
  /** Tem o produto/direito contratado (mesmo no limite)? */
  function hasEntitlement(cap: 'mobile' | 'tag' | 'geofence'): boolean {
    const e = entitlements.value
    if (!e) return false
    const limit = cap === 'mobile' ? e.mobile_limit : cap === 'tag' ? e.tag_limit : e.geofence_limit
    return limit === null || limit > 0
  }

  /** Re-lê /account após ação que muda billing/direitos (compra, aceite). */
  async function refreshAccount(): Promise<void> {
    const res = await api.value!.accounts.current()
    currentAccount.value = res.data
  }

  /** Registra aceite dos termos pendentes e atualiza o estado da conta. */
  async function acceptPendingTerms(terms: { kind: PendingTerm['kind']; version: number }[]): Promise<void> {
    await api.value!.terms.accept(terms)
    await refreshAccount()
  }

  function bind(client: LocziApi) {
    api.value = client
  }

  function signal(): AbortSignal {
    const c = new AbortController()
    inflight.add(c)
    c.signal.addEventListener('abort', () => inflight.delete(c), { once: true })
    return c.signal
  }

  /** Cancela requisições em voo e limpa dados em memória (§12.6). */
  function resetContext() {
    for (const c of inflight) c.abort()
    inflight.clear()
  }

  async function bootstrap(): Promise<void> {
    try {
      const res = await api.value!.auth.me()
      user.value = res.data
      // Restaura conta previamente escolhida neste cliente (localStorage é
      // apenas conveniência de UI — autorização sempre revalidada no servidor).
      const saved = localStorage.getItem('loczi.account')
      const found = res.data.accounts.find(a => a.id === saved) ?? res.data.accounts[0]
      if (found) await applyAccount(found.id)
    } catch {
      user.value = null
      currentAccount.value = null
    } finally {
      ready.value = true
    }
  }

  async function login(email: string, password: string): Promise<void> {
    const res = await api.value!.auth.login({ email, password })
    user.value = res.data
    const found = res.data.accounts[0]
    if (found) await applyAccount(found.id)
  }

  async function register(input: { name: string; email: string; password: string; password_confirmation: string }) {
    const res = await api.value!.auth.register(input)
    user.value = res.data
    const found = res.data.accounts[0]
    if (found) await applyAccount(found.id)
  }

  async function applyAccount(publicId: string): Promise<void> {
    await api.value!.accounts.select(publicId)
    const res = await api.value!.accounts.current()
    currentAccount.value = res.data
    api.value!.setAccount(res.data.id)
    localStorage.setItem('loczi.account', res.data.id)
  }

  /** Troca de conta: cancela em voo, limpa contexto, carrega a nova (§12.6). */
  async function switchAccount(publicId: string): Promise<void> {
    switching.value = true
    resetContext()
    try {
      await applyAccount(publicId)
    } finally {
      switching.value = false
    }
  }

  /** Sessão morreu no servidor (401): limpa o estado sem chamar a API — o
   *  guard passa a ver não-autenticado e o redirect pro login é real. */
  function expire(): void {
    resetContext()
    user.value = null
    currentAccount.value = null
    api.value?.setAccount(null)
    localStorage.removeItem('loczi.account')
  }

  async function logout(): Promise<void> {
    try {
      await api.value!.auth.logout()
    } finally {
      resetContext()
      user.value = null
      currentAccount.value = null
      api.value?.setAccount(null)
      localStorage.removeItem('loczi.account')
    }
  }

  return {
    api, user, currentAccount, ready, switching,
    isAuthenticated, accounts, canManageTags, canManageGeofences, canViewBilling, canManageAccount,
    billing, entitlements, usage, team, access, contracted, pendingTerms, serviceBlocked, hasNoAccount, billingGrace, courtesyActive,
    canUse, hasEntitlement, refreshAccount, acceptPendingTerms,
    bind, bootstrap, login, register, switchAccount, logout, expire, signal,
  }
})
